import { ButtonBase } from '@material-ui/core';
import { mergeClasses } from '@material-ui/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import useForkRef from '~utils/use-fork-ref';
import { useButtonStyles } from './button-style';
import Loading from './loading';

/**
 * @param props.color {'primary' | 'secondary' | 'danger'}
 * 'Secondary' is the default value.
 * For backward compatibility, if `color` is 'default', then consider its value is 'secondary'
 */
const Button = React.forwardRef((props, refProp) => {
  const {
    children,
    variant,
    color,
    size,
    loading,
    onClick,
    classes: classesProp,
    style,
    ...otherProps
  } = props;
  const ref = useRef();

  const handleClick = (e) => {
    if (onClick && !loading) onClick(e);
  };

  const styles = useButtonStyles(variant);

  const classes = {
    root: classnames(styles.root, {
      [styles.colorSecondary]: !color || color === 'secondary',
      [styles.colorPure]: color === 'pure',
      [styles.colorPrimary]: color === 'primary',
      [styles.colorDanger]: color === 'danger',
      [styles.colorGreen]: color === 'green',

      [styles.sizeDefault]: !size || size === 'default',
      [styles.sizeSmall]: size === 'small',
      [styles.sizeLarge]: size === 'large',
    }),
    disabled: styles.disabled,
  };

  const getLoadingColor = () => {
    switch (variant) {
      default: {
        switch (color) {
          default:
          case 'secondary':
          case 'default': {
            return 'grey66';
          }
          case 'danger':
          case 'green':
          case 'confirm':
          case 'primary': {
            return 'white';
          }
        }
      }
      case 'text':
      case 'icon': {
        switch (color) {
          case 'primary': {
            return 'brandAction';
          }
          case 'default':
          default: {
            return 'grey66';
          }
        }
      }
    }
  };

  const loadingStyle = loading
    ? { width: ref.current?.offsetWidth, height: ref.current?.offsetHeight }
    : {};

  return (
    <ButtonBase
      disableRipple
      {...otherProps}
      style={{ ...loadingStyle, ...style }}
      onClick={handleClick}
      ref={useForkRef(ref, refProp)}
      classes={mergeClasses({ baseClasses: classes, newClasses: classesProp })}
    >
      {loading ? <Loading minHeight="unset" color={getLoadingColor()} size={4} /> : children}
    </ButtonBase>
  );
});

Button.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  style: PropTypes.object,
  children: PropTypes.node,
  variant: PropTypes.oneOf(['base', 'text', 'icon']),
  color: PropTypes.string,
  size: PropTypes.oneOf(['default', 'large', 'small']),
  loading: PropTypes.bool,
  onClick: PropTypes.func,
};

Button.defaultProps = {
  classes: undefined,
  style: undefined,
  children: null,
  variant: undefined,
  color: undefined,
  size: undefined,
  loading: undefined,
  onClick: undefined,
};

export default Button;
